import { useEffect, useState } from 'react';
import type { Agent, CreateTaxonomyLabel, TaxonomyLabel } from '@orqforge/shared';
import { parseTaxonomyJson } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';

interface TaxonomyDraft {
  uid: string;
  key: string;
  name: string;
  description: string;
  keywords: string;
  targetAgentId: string;
  priority: number;
}

function emptyDraft(): TaxonomyDraft {
  return {
    uid: crypto.randomUUID(),
    key: '',
    name: '',
    description: '',
    keywords: '',
    targetAgentId: '',
    priority: 0,
  };
}

function labelsToDrafts(labels: CreateTaxonomyLabel[]): TaxonomyDraft[] {
  return labels.map((row) => ({
    uid: crypto.randomUUID(),
    key: row.key,
    name: row.name,
    description: row.description ?? '',
    keywords: (row.routing?.keywords ?? []).join(', '),
    targetAgentId: row.routing?.targetAgentId ?? '',
    priority: row.routing?.priority ?? 0,
  }));
}

function draftsToLabels(drafts: TaxonomyDraft[]): CreateTaxonomyLabel[] {
  return drafts.map((d) => ({
    key: d.key.trim(),
    name: d.name.trim(),
    description: d.description.trim(),
    routing: {
      keywords: d.keywords.split(',').map((k) => k.trim()).filter(Boolean),
      targetAgentId: d.targetAgentId || undefined,
      priority: Number(d.priority) || 0,
    },
  }));
}

function stringifyLabels(labels: CreateTaxonomyLabel[]): string {
  return JSON.stringify(labels, null, 2);
}

function keywordChips(raw: string): string[] {
  return raw.split(',').map((k) => k.trim()).filter(Boolean);
}

export function TaxonomyPage() {
  const { workspaces, workspaceId } = useApp();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonText, setJsonText] = useState('[]');
  const [drafts, setDrafts] = useState<TaxonomyDraft[]>([]);
  const [pendingUid, setPendingUid] = useState<string | null>(null);

  function applyLabels(labels: TaxonomyLabel[] | CreateTaxonomyLabel[]) {
    const payload = labels.map((row) => ({
      key: row.key,
      name: row.name,
      description: row.description,
      routing: row.routing,
    }));
    setDrafts(labelsToDrafts(payload));
    setJsonText(stringifyLabels(payload));
    setJsonError(null);
  }

  useEffect(() => {
    if (!workspaceId) return;
    void apiClient.listTaxonomy().then(applyLabels).catch(() => applyLabels([]));
    void apiClient.listAgents().then((r) => setAgents(r.items)).catch(() => setAgents([]));
  }, [workspaceId]);

  const canSave = Boolean(
    workspaceId &&
      !saving &&
      (mode === 'json' ? !jsonError : drafts.every((d) => d.key.trim() && d.name.trim())),
  );

  function switchMode(next: 'form' | 'json') {
    if (next === mode) return;
    if (next === 'json') {
      setJsonText(stringifyLabels(draftsToLabels(drafts)));
      setJsonError(null);
      setMode('json');
      return;
    }
    const parsed = parseTaxonomyJson(jsonText);
    if (parsed.error) {
      setJsonError(parsed.error);
      return;
    }
    setDrafts(labelsToDrafts(parsed.labels));
    setJsonError(null);
    setMode('form');
  }

  function onJsonChange(value: string) {
    setJsonText(value);
    const parsed = parseTaxonomyJson(value);
    setJsonError(parsed.error ?? null);
  }

  function patch(uid: string, partial: Partial<TaxonomyDraft>) {
    setDrafts((rows) => rows.map((d) => (d.uid === uid ? { ...d, ...partial } : d)));
  }

  async function saveDocument() {
    if (!canSave) return;
    let labels: CreateTaxonomyLabel[];
    if (mode === 'json') {
      const parsed = parseTaxonomyJson(jsonText);
      if (parsed.error) {
        setJsonError(parsed.error);
        return;
      }
      labels = parsed.labels;
    } else {
      labels = draftsToLabels(drafts);
    }
    setSaving(true);
    try {
      const saved = await apiClient.replaceTaxonomy(labels);
      applyLabels(saved);
    } catch {
      /* keep the editor */
    } finally {
      setSaving(false);
    }
  }

  const agentOptions = [{ value: '', label: 'No route' }, ...agents.map((a) => ({ value: a.id, label: a.name }))];

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Taxonomy</h1>
          <p className="text-sm text-gray-500">
            Workspace routing document for <strong>{workspace?.name || 'no workspace'}</strong>. Edit as a form or as JSON; both views are the same array of labels.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <Button size="sm" variant={mode === 'form' ? 'default' : 'ghost'} className="rounded-none" onClick={() => switchMode('form')}>Form</Button>
            <Button size="sm" variant={mode === 'json' ? 'default' : 'ghost'} className="rounded-none" onClick={() => switchMode('json')}>JSON</Button>
          </div>
          <Button size="sm" disabled={!canSave} onClick={() => void saveDocument()}>
            {saving ? 'Saving...' : 'Save taxonomy'}
          </Button>
        </div>
      </div>

      {!workspaceId ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          Select a workspace from the sidebar to manage its taxonomy.
        </div>
      ) : mode === 'json' ? (
        <div className="mt-4">
          {jsonError ? (
            <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
              {jsonError}
            </div>
          ) : (
            <p className="mb-2 text-xs text-gray-500">JSON array of labels. Invalid JSON blocks Save and switching back to Form.</p>
          )}
          <textarea
            className="min-h-[28rem] w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 font-mono text-sm leading-relaxed text-slate-200"
            value={jsonText}
            spellCheck={false}
            onChange={(e) => onJsonChange(e.target.value)}
          />
        </div>
      ) : (
        <div className="mt-4 flex max-w-4xl flex-col gap-4">
          {drafts.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
              No labels yet. Add one so intake can classify and route.
            </div>
          )}
          {drafts.map((d) => (
            <div key={d.uid} className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold">{d.name || d.key || 'New label'}</h2>
                  <p className="text-xs text-gray-400">Intake matching this label routes to the agent you pick below.</p>
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setPendingUid(d.uid)}>Remove</Button>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_7rem]">
                <label className="text-xs font-medium text-gray-500">
                  Key
                  <Input className="mt-1 font-mono" value={d.key} autoComplete="off" placeholder="ap-invoice" onChange={(e) => patch(d.uid, { key: e.target.value })} />
                  <span className="mt-1 block text-[11px] font-normal text-gray-400">Stable id. Lowercase, no spaces.</span>
                </label>
                <label className="text-xs font-medium text-gray-500">
                  Name
                  <Input className="mt-1" value={d.name} autoComplete="off" placeholder="AP Invoice" onChange={(e) => patch(d.uid, { name: e.target.value })} />
                </label>
                <label className="text-xs font-medium text-gray-500">
                  Priority
                  <Input className="mt-1" type="number" value={d.priority} autoComplete="off" onChange={(e) => patch(d.uid, { priority: Number(e.target.value) })} />
                  <span className="mt-1 block text-[11px] font-normal text-gray-400">Higher wins ties.</span>
                </label>
              </div>
              <label className="mt-4 block text-xs font-medium text-gray-500">
                Description
                <textarea
                  className="mt-1 min-h-28 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed dark:border-gray-600 dark:bg-gray-800"
                  rows={4}
                  value={d.description}
                  placeholder="What this category covers. Shown to operators; keywords below drive routing."
                  onChange={(e) => patch(d.uid, { description: e.target.value })}
                />
              </label>
              <label className="mt-4 block text-xs font-medium text-gray-500">
                Keywords
                <textarea
                  className="mt-1 min-h-20 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed dark:border-gray-600 dark:bg-gray-800"
                  rows={3}
                  value={d.keywords}
                  placeholder="invoice, accounts payable, payment proposal"
                  onChange={(e) => patch(d.uid, { keywords: e.target.value })}
                />
                <span className="mt-1 block text-[11px] font-normal text-gray-400">Comma separated. Case does not matter.</span>
                {keywordChips(d.keywords).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {keywordChips(d.keywords).map((kw) => (
                      <Badge key={kw} variant="secondary">{kw}</Badge>
                    ))}
                  </div>
                )}
              </label>
              <div className="mt-4">
                <span className="mb-1.5 block text-xs font-medium text-gray-500">Route to</span>
                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(11rem,1fr))' }}>
                  {agentOptions.map((option) => {
                    const selected = d.targetAgentId === option.value;
                    return (
                      <button
                        key={option.value || 'none'}
                        type="button"
                        className={`rounded-xl border p-3 text-left text-sm ${selected ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600/60 dark:bg-blue-950/40' : 'border-gray-200 hover:border-blue-400 dark:border-gray-700'}`}
                        onClick={() => patch(d.uid, { targetAgentId: option.value })}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-fit" onClick={() => setDrafts((rows) => [...rows, emptyDraft()])}>
            Add label
          </Button>
        </div>
      )}

      <Dialog open={pendingUid !== null} onOpenChange={(open) => { if (!open) setPendingUid(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove label</DialogTitle></DialogHeader>
          <DialogBody>
            <p className="text-sm">Remove this label from the document? Save taxonomy to persist.</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingUid(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!pendingUid) return;
                setDrafts((rows) => rows.filter((d) => d.uid !== pendingUid));
                setPendingUid(null);
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
